import { useRef, useEffect, useState } from "preact/hooks";
import { useWidget, ACTIVE_STATE, MINIMIZED_STATE, HIDDEN_STATE } from "../providers/WidgetProvider";
import { useMute } from "../providers/MuteProvider";
import { useUserInteraction } from "../providers/UserInteractionProvider";
import { useParticipantIds } from "@daily-co/daily-react";
import DailyPlayer from "./DailyPlayer";
import LoadingScreen from "./LoadingScreen";
import ErrorScreen from "./ErrorScreen";
import { useAgent } from "@/providers/AgentProvider";
import { useError } from "../providers/ErrorProvider";
import ActionBar from "./ActionBar";
import ErrorToast from "./ErrorToast";
import { X, Minimize2, Play } from "lucide-react";
import { XMarkIcon } from "@heroicons/react/16/solid";

export default function VideoChat() {
  const { setWidgetState, widgetState, isAgentVideoReady, showMinimizeButton } = useWidget();
  const { agentData } = useAgent();
  const { isAudioMuted, setIsAudioMuted, setIsMicMuted } = useMute();
  const { hasUserInteracted } = useUserInteraction();
  const { errorMsg, setErrorMsg, nonFatalErrorMsg } = useError();
  const { resetDailyState } = useWidget();
  const remoteParticipantIds = useParticipantIds({ filter: "remote" });
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [showDailyPlayerDelayed, setShowDailyPlayerDelayed] = useState(false);
  const dailyPlayerDelayTimeoutRef = useRef<number | null>(null);

  const firstVideoUrl = agentData?.first_video_url;

  // Restart intro video when state changes to minimized
  useEffect(() => {
    if (widgetState === MINIMIZED_STATE && videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(error => {
        console.error("Error restarting video:", error);
      });
    }
  }, [widgetState]);

  // Delay DailyPlayer transition by 200ms when hasAgentJoinedCall becomes true
  useEffect(() => {
    const hasAgentJoinedCall = remoteParticipantIds.length > 0 && isAgentVideoReady;

    if (hasAgentJoinedCall) {
      // Clear any existing timeout
      if (dailyPlayerDelayTimeoutRef.current !== null) {
        clearTimeout(dailyPlayerDelayTimeoutRef.current);
      }
      // Set delayed state after 200ms
      dailyPlayerDelayTimeoutRef.current = window.setTimeout(() => {
        setShowDailyPlayerDelayed(true);
        dailyPlayerDelayTimeoutRef.current = null;
      }, 300);
    } else {
      // Reset immediately when agent leaves
      setShowDailyPlayerDelayed(false);
      if (dailyPlayerDelayTimeoutRef.current !== null) {
        clearTimeout(dailyPlayerDelayTimeoutRef.current);
        dailyPlayerDelayTimeoutRef.current = null;
      }
    }

    return () => {
      if (dailyPlayerDelayTimeoutRef.current !== null) {
        clearTimeout(dailyPlayerDelayTimeoutRef.current);
        dailyPlayerDelayTimeoutRef.current = null;
      }
    };
  }, [remoteParticipantIds.length, isAgentVideoReady]);

  const handleCloseClick = () => {
    setWidgetState(MINIMIZED_STATE);
    setIsAudioMuted(true);
    setIsMicMuted(true);
    if (errorMsg) {
      setErrorMsg("");
      resetDailyState();
    }
  };

  const handleMinimizeClick = () => {
    setWidgetState(HIDDEN_STATE);
  };

  const handlePlayClick = () => {
    setWidgetState(ACTIVE_STATE);
    setIsAudioMuted(false);
    // If we hit an error while minimized, reset the daily state and clear the error message
    // So that we can retry connecting to the call
    if (errorMsg) {
      setErrorMsg("");
      resetDailyState();
    }
  };

  const hasAgentJoinedCall = remoteParticipantIds.length > 0 && isAgentVideoReady;
  const showActionbar =
    widgetState === ACTIVE_STATE &&
    (!hasUserInteracted ||
      hasAgentJoinedCall ||
      (widgetState === ACTIVE_STATE && !hasAgentJoinedCall && hasUserInteracted && !errorMsg)) && // loading screen state
    !errorMsg;
  const showIntroVideo =
    widgetState === MINIMIZED_STATE || widgetState === HIDDEN_STATE || (!hasUserInteracted && !errorMsg);
  const showLoadingScreen = widgetState === ACTIVE_STATE && !showDailyPlayerDelayed && hasUserInteracted && !errorMsg;
  const showDailyPlayer = widgetState === ACTIVE_STATE && showDailyPlayerDelayed && hasUserInteracted && !errorMsg;
  const showErrorScreen = widgetState === ACTIVE_STATE && errorMsg;
  const showErrorToast = widgetState === ACTIVE_STATE && hasUserInteracted && nonFatalErrorMsg;

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`relative mb-1 rounded-3xl shadow-4xl overflow-hidden transition-all ${widgetState === HIDDEN_STATE ? "duration-300 ease-in" : "duration-300 ease-out"} bg-black ${
        widgetState === ACTIVE_STATE ? "w-[252px] h-[377px]" : "w-[144px] h-[216px]"
      }`}
      style={
        widgetState === HIDDEN_STATE
          ? { transform: "translateX(100%) scale(0.8, 0.5)", filter: "blur(12px)", opacity: 0, pointerEvents: "none" }
          : { transform: "translateX(0) scale(1)", filter: "blur(0px)", opacity: 1 }
      }
    >
      {/* Close/Minimize button */}
      {(widgetState === ACTIVE_STATE || showMinimizeButton) && (
        <button
          onClick={widgetState === ACTIVE_STATE ? handleCloseClick : handleMinimizeClick}
          className={`
            absolute p-2 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20 backdrop-blur-xl cursor-pointer z-10 transition-colors h-11 w-11 shadow-inset-white-10
            transition-opacity duration-200
            ${widgetState == ACTIVE_STATE ? "top-2 right-2 " : "bottom-2 left-[calc(50%-22px)]"} 
            ${isHovered || widgetState === ACTIVE_STATE ? "opacity-100" : "opacity-0"}
          `}
          aria-label={widgetState === ACTIVE_STATE ? "Close" : "Minimize"}
        >
          {widgetState === ACTIVE_STATE ? (
            // <X strokeWidth={2} size={24} color="white" />
            <XMarkIcon className="size-6 text-white" />
          ) : (
            <Minimize2 strokeWidth={2} size={20} color="white" />
          )}
        </button>
      )}

      {showErrorToast && <ErrorToast errorMsg={nonFatalErrorMsg} />}

      <div className="absolute inset-0 w-full h-full">
        {/* Intro video */}
        {showIntroVideo && (
          <video
            ref={videoRef}
            src={firstVideoUrl}
            className="absolute inset-0 w-full h-full object-cover"
            playsInline
            autoPlay
            muted={widgetState !== ACTIVE_STATE || isAudioMuted}
            loop
          />
        )}
        {/* Loading screen */}
        {showLoadingScreen && <LoadingScreen />}
        {/* DailyPlayer overlay */}
        {showDailyPlayer && <DailyPlayer />}
        {/* Error screen */}
        {showErrorScreen && <ErrorScreen />}
      </div>

      {/* Play button */}
      {widgetState === MINIMIZED_STATE && (
        <button
          onClick={handlePlayClick}
          className="absolute inset-0 flex items-center justify-center cursor-pointer"
          aria-label="Play video"
        >
          <span className="opacity-100 hover:opacity-80 transition-opacity ">
            <Play size={40} fill="white" color="none"></Play>
          </span>
        </button>
      )}

      {/* Message Input */}
      {showActionbar && <ActionBar />}

      <div className="pointer-events-none absolute inset-0 rounded-3xl shadow-inset-white-20" />
    </div>
  );
}
